import { z } from 'zod'

// ── Protocol ──

export const PROTOCOL_VERSION = 1 as const

export const SOURCE_WIDGET = 'marbles-game-widget' as const
export const SOURCE_OPERATOR = 'marbles-operator' as const

// ── Envelope ──

export interface PostMessageEnvelope<T = unknown> {
  source: typeof SOURCE_WIDGET | typeof SOURCE_OPERATOR
  type: string
  version: typeof PROTOCOL_VERSION
  payload: T
  timestamp: number
  requestId?: string
}

export const InboundEnvelopeSchema = z.object({
  source: z.literal(SOURCE_OPERATOR),
  type: z.string(),
  version: z.literal(PROTOCOL_VERSION),
  payload: z.record(z.unknown()).default({}),
  timestamp: z.number(),
  requestId: z.string().optional()
})

// ── Inbound Command Types ──

export const InboundCommandType = {
  REFRESH_BALANCE: 'refresh-balance',
  MUTE_AUDIO: 'mute-audio',
  SET_AUDIO_VOLUME: 'set-audio-volume',
  OPEN_GAME_HISTORY: 'open-game-history',
  CLOSE_PANELS: 'close-panels',
  GET_STATE: 'get-state'
} as const

export type InboundCommandType = (typeof InboundCommandType)[keyof typeof InboundCommandType]

// ── Inbound Payloads ──

export const MuteAudioPayloadSchema = z.object({
  muted: z.boolean()
})

export const SetAudioVolumePayloadSchema = z.object({
  volume: z.number().min(0).max(1)
})

export type MuteAudioPayload = z.infer<typeof MuteAudioPayloadSchema>
export type SetAudioVolumePayload = z.infer<typeof SetAudioVolumePayloadSchema>

// ── Outbound Event Types ──

export const OutboundEventType = {
  // Lifecycle
  GAME_LOADED: 'game:loaded',
  GAME_READY: 'game:ready',
  GAME_ERROR: 'game:error',

  // Round lifecycle
  ROUND_BETTING_OPEN: 'round:betting-open',
  ROUND_BETTING_CLOSED: 'round:betting-closed',
  ROUND_STARTED: 'round:started',
  ROUND_ENDED: 'round:ended',

  // Bets
  BET_PLACED: 'bet:placed',
  BET_REJECTED: 'bet:rejected',
  BET_RESULT: 'bet:result',

  // Balance
  BALANCE_CHANGED: 'balance:changed',
  BALANCE_INSUFFICIENT: 'balance:insufficient',

  // Cashout
  CASHOUT_COMPLETED: 'cashout:completed',

  // Auto-bet
  AUTOBET_STARTED: 'autobet:started',
  AUTOBET_STOPPED: 'autobet:stopped',

  // Session
  SESSION_EXPIRED: 'session:expired',

  // Idle
  PLAYER_IDLE_WARNING: 'player:idle-warning',
  PLAYER_IDLE_PAUSED: 'player:idle-paused',

  // State query response
  STATE_SNAPSHOT: 'state-snapshot'
} as const

export type OutboundEventType = (typeof OutboundEventType)[keyof typeof OutboundEventType]

// ── Outbound Payloads ──

export interface GameLoadedPayload {
  gameId: string
  gameType: string
  playerId: string
}

export interface GameReadyPayload {
  gameId: string
  roundId: string
}

export interface GameErrorPayload {
  code: string
  message: string
}

export interface RoundBettingOpenPayload {
  roundId: string
  gameId: string
}

export interface RoundBettingClosedPayload {
  roundId: string
  gameId: string
}

export interface RoundStartedPayload {
  roundId: string
  gameId: string
  totalBetAmount?: number
  currency?: string
}

export interface RoundEndedPayload {
  roundId: string
  gameId: string
  crashMultiplier?: number
  totalBetAmount?: number
  totalWinAmount?: number
  currency?: string
}

export interface BetPlacedPayload {
  betId: string
  amount: number
  currency: string
  betType: string
}

export interface BetRejectedPayload {
  reason: string
  amount: number
  currency: string
}

export interface BetResultPayload {
  betId: string
  status: 'won' | 'lost' | 'cashed_out' | 'voided'
  payout?: number
  currency: string
  multiplier?: number
}

export interface BalanceChangedPayload {
  amount: number
  currency: string
}

export interface BalanceInsufficientPayload {
  requiredAmount?: number
  currentBalance: number
  currency: string
}

export interface CashoutCompletedPayload {
  betId: string
  multiplier: number
  payout: number
  currency: string
}

export interface AutobetStartedPayload {
  betAmount: number
  currency: string
}

export interface AutobetStoppedPayload {
  reason: string
}

export interface PlayerIdlePayload {
  gameId: string
}

export interface StateSnapshotPayload {
  isLoaded: boolean
  playerId?: string
  gameId?: string
  balance?: { amount: number; currency: string }
  isMuted: boolean
  hasActiveBet: boolean
}
